import ActionButton from "components/common/ActionButton";
import { useFetchClinics } from "hooks/useFetchClinics";
import { useFetchContacts } from "hooks/useFetchContacts";
import { useFetchPhysicians } from "hooks/useFetchPhysicians";
import { useFetchRx } from "hooks/useFetchRx";
import React, { useEffect } from "react";
import {
  Button,
  Card,
  Col,
  FloatingLabel,
  Form,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  Controller,
  useFieldArray,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import CreatableSelect from "react-select/creatable";

const VisitBasicInformation = () => {
  const {
    register,
    watch,
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();

  const {
    fields: prescriptions,
    append: appendPrescriptions,
    remove: removePrescriptions,
    move,
  } = useFieldArray({
    control,
    name: "prescriptions",
  });

  const followupRequired = !!useWatch({ control, name: "followupRequired" });
  const clinicIdWatch = watch("clinicId");
  const pyhysicianWatch = watch("doctorId");

  const navigate = useNavigate();
  const { state } = useLocation();

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderColor: errors.type ? "red" : provided.borderColor,
      "&:hover": {
        borderColor: errors.type ? "red" : provided["&:hover"].borderColor,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "black", // Set the text color of the selected value to black
      padding: "7px",
    }),
    placeholder: (provided) => ({
      ...provided,
      padding: "0px 7px",
    }),
    input: (provided) => ({
      ...provided,
      color: "black",
      // paddingTop: "12px",
      // paddingBottom: "12px",
      paddingLeft: "7px",
      paddingRight: "7px",
      minWidth: "200px",
      // padding: "12px", // Set the input text color to black
    }),

    menu: (provided) => ({ ...provided, color: "black", zIndex: 2 }),
    menuList: (provided) => ({ ...provided, padding: 0, zIndex: 1 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  };

  const getSelectStyles = (hasError) => ({
    control: (provided) => ({
      ...provided,
      borderColor: hasError ? "red" : provided.borderColor,
      boxShadow: hasError
        ? "0 0 0 0.2rem rgba(255, 0, 0, 0.25)"
        : provided.boxShadow,
      "&:hover": {
        borderColor: hasError ? "red" : provided.borderColor,
      },
    }),
    singleValue: (provided) => ({
      ...provided,
      color: "black",
      padding: "7px",
    }),
    placeholder: (provided) => ({
      ...provided,
      padding: "0px 7px",
    }),
    input: (provided) => ({
      ...provided,
      color: "black",
      paddingLeft: "7px",
      paddingRight: "7px",
      minWidth: "200px",
    }),
    menu: (provided) => ({ ...provided, color: "black", zIndex: 2 }),
    menuList: (provided) => ({ ...provided, padding: 0, zIndex: 1 }),
    menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
  });

  const {
    data: clinics,
    isLoading: isClinicLoading,
    isFetching: isClinicFetching,
  } = useFetchClinics();

  let clinicsData = clinics?.data?.data ?? [];

  const {
    data: physicians,
    isLoading: isPhysiciansLoading,
    isFetching: isPhysiciansFetching,
  } = useFetchPhysicians(clinicIdWatch || 0);

  let physiciansData = physicians?.data?.data ?? [];

  useEffect(() => {
    const ph = physiciansData?.find((d) => d.id == pyhysicianWatch);
    setValue("visitFee", ph?.fee);
  }, [pyhysicianWatch, setValue]);

  const {
    data: rx,
    isLoading: isRxLoading,
    isFetching: isRxFetching,
  } = useFetchRx();

  let rxData = rx?.data?.data ?? [];

  // console.log("rxData", rxData);

  const rxOptions = rxData?.map((item) => ({
    label: item.name,
    value: item.name,
  }));

  if (isRxLoading || isClinicLoading || isPhysiciansLoading) {
    return <Spinner animation="border" variant="primary" />;
  }

  return (
    <>
      {/* <Card.Header as="h6" className="bg-body-tertiary mb-2">
        Basic information
      </Card.Header> */}
      {/* <FormProvider {...methods}>
        <Form onSubmit={handleSubmit(onSubmit)}> */}
      <Row className="gx-2 gy-3 mb-2">
        <Col md="6">
          <Form.Group>
            <FloatingLabel
              controlId="floatingSelectGrid"
              label={
                <span>
                  Clinic
                  {/* <span style={{ color: "red" }}>*</span> */}
                </span>
              }
            >
              <Form.Select
                aria-label="clinicId"
                isInvalid={!!errors.clinicId}
                {...register("clinicId")}
              >
                <option value="">Select</option>
                {clinicsData?.map((cl) => (
                  <option key={cl.id} value={cl.id}>
                    {cl.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.clinicId?.message}
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>
        </Col>
        <Col md="6">
          <Form.Group>
            <FloatingLabel
              controlId="floatingSelectGrid"
              label={
                <span>
                  Physician
                  {/* <span style={{ color: "red" }}>*</span> */}
                </span>
              }
            >
              <Form.Select
                aria-label="doctorId"
                disabled={!clinicIdWatch}
                isInvalid={!!errors.doctorId}
                {...register("doctorId")}
              >
                <option value="">Select</option>
                {physiciansData?.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.doctorId?.message}
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>
        </Col>
      </Row>
      <Row className="gx-2 gy-3">
        <Col md="12">
          <Form.Group>
            <FloatingLabel controlId="floatingTextarea2" label="Diagnosis">
              <Form.Control
                as="textarea"
                placeholder="Diagnosis"
                style={{ height: "100px" }}
                isInvalid={!!errors.diagnosis}
                {...register("diagnosis")}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 mt-3">
        <div
          style={{
            maxHeight: `calc(100vh - 20.7rem)`,
            overflowY: "auto",
          }}
        >
          <Table style={{ borderCollapse: "separate", borderSpacing: 0 }}>
            {/* 🔹 Fixed Header */}
            <thead
              className="bg-200"
              style={{
                position: "sticky",
                top: 0,
                zIndex: 10,
                background: "#fff",
              }}
            >
              <tr>
                <th
                  scope="col"
                  className="fs-10 text-center"
                  // style={{ width: "140px" }}
                >
                  #
                </th>
                <th scope="col" className="fs-10">
                  Medication Name
                </th>
                <th scope="col" className="fs-10">
                  Dosage
                </th>
                <th scope="col" className="fs-10">
                  Frequency
                </th>
                <th scope="col" className="fs-10">
                  Duration
                </th>
                <th scope="col" className="fs-10">
                  Instructions
                </th>
                <th
                  scope="col"
                  style={{ padding: "9px", width: "10px" }}
                  className="text-end"
                >
                  <ActionButton
                    icon="plus"
                    variant="success"
                    title="Add"
                    className="px-1 py-0"
                    onClick={() => appendPrescriptions({})}
                  />
                </th>
              </tr>
            </thead>

            {/* 🔹 Table Body */}
            <tbody>
              {prescriptions.map((item, index) => (
                <tr key={item.id || index}>
                  <td className="text-center">{index + 1}</td>
                  <td style={{ padding: "10px" }}>
                    <Form.Group>
                      <Controller
                        name={`prescriptions[${index}].medicationName`}
                        control={control}
                        render={({ field }) => (
                          <CreatableSelect
                            {...field}
                            options={rxOptions} // Replace with real options
                            isClearable
                            tabIndex={4}
                            placeholder="Medication Name"
                            styles={getSelectStyles(
                              !!errors.prescriptions?.[index]?.medicationName
                            )}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            onChange={(selectedOption) =>
                              field.onChange(
                                selectedOption ? selectedOption.value : ""
                              )
                            }
                            value={
                              field.value
                                ? { label: field.value, value: field.value }
                                : null
                            }
                          />
                        )}
                      />
                      {errors.prescriptions?.[index]?.medicationName && (
                        <div className="invalid-feedback d-block">
                          {
                            errors.prescriptions?.[index]?.medicationName
                              ?.message
                          }
                        </div>
                      )}

                      {/* <CreatableSelect
                        options={[]}
                        isClearable
                        placeholder={
                          <span>
                            Medication Name
                            <span style={{ color: "red", fontSize: "12px" }}>
                              *
                            </span>
                          </span>
                        }
                        // className={`${errors.type ? "is-invalid" : ""}`}
                        styles={customStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        // onChange={handleSelectChange}
                        // onFocus={handleInputFocus}
                        // onInputChange={handleInputFocus}
                        // defaultValue={initialSelectedOption}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.prescriptions?.[index]?.medicationName?.message}
                      </Form.Control.Feedback> */}
                    </Form.Group>
                  </td>

                  <td style={{ paddingTop: "10px" }}>
                    <Form.Control
                      type="text"
                      placeholder="Dosage"
                      tabIndex={5}
                      isInvalid={!!errors.prescriptions?.[index]?.dosage}
                      {...register(`prescriptions[${index}].dosage`)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.prescriptions?.[index]?.dosage?.message}
                    </Form.Control.Feedback>
                  </td>
                  <td style={{ paddingTop: "10px" }}>
                    <Form.Control
                      type="text"
                      placeholder="Frequency"
                      isInvalid={!!errors.prescriptions?.[index]?.frequency}
                      {...register(`prescriptions[${index}].frequency`)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.prescriptions?.[index]?.frequency?.message}
                    </Form.Control.Feedback>
                  </td>
                  <td style={{ paddingTop: "10px" }}>
                    <Form.Control
                      type="text"
                      placeholder="Duration"
                      isInvalid={!!errors.prescriptions?.[index]?.duration}
                      {...register(`prescriptions[${index}].duration`)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.prescriptions?.[index]?.duration?.message}
                    </Form.Control.Feedback>
                  </td>
                  <td style={{ paddingTop: "10px" }}>
                    <Form.Control
                      type="text"
                      placeholder="Instructions"
                      isInvalid={!!errors.prescriptions?.[index]?.instructions}
                      {...register(`prescriptions[${index}].instructions`)}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.prescriptions?.[index]?.instructions?.message}
                    </Form.Control.Feedback>
                  </td>

                  <td
                    style={{ paddingLeft: "9px", maxWidth: "10px" }}
                    className="text-center"
                  >
                    <ActionButton
                      icon="trash-alt"
                      title="Delete"
                      variant="danger"
                      className="px-1 py-0"
                      onClick={() => removePrescriptions(index)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </Row>

      <Row className="gx-2 gy-3 my-1">
        <Col md="12">
          <Form.Group>
            <FloatingLabel controlId="floatingTextarea2" label="Notes">
              <Form.Control
                as="textarea"
                placeholder="Notes"
                style={{ height: "100px" }}
                isInvalid={!!errors.notes}
                {...register("notes")}
              />
            </FloatingLabel>
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 gy-3 my-1">
        <Col md="2">
          <Controller
            name="followupRequired"
            control={control}
            defaultValue={!!state?.followupRequired}
            render={({ field: { onChange, value } }) => (
              <Form.Check
                type="switch"
                id="followupSwitch"
                style={{ marginTop: "14px" }}
                label="Followup Required"
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                isInvalid={!!errors.followupRequired}
              />
            )}
          />
        </Col>

        {followupRequired && (
          <Col md="2">
            <Form.Group>
              <FloatingLabel
                controlId="floatingInput"
                label={
                  <span>
                    Next Visit On <span style={{ color: "red" }}>*</span>
                  </span>
                }
                className=""
              >
                <Form.Control
                  type="date"
                  placeholder=""
                  autoComplete="off"
                  isInvalid={!!errors.nextVisitOn}
                  {...register("nextVisitOn")}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.nextVisitOn?.message}
                </Form.Control.Feedback>
              </FloatingLabel>
            </Form.Group>
          </Col>
        )}

        <Col style={{ display: "flex", justifyContent: "end" }}>
          <Form.Group>
            <FloatingLabel
              controlId="floatingInput"
              label={<span>Fee</span>}
              className=""
            >
              <Form.Control
                type="text"
                placeholder=""
                autoComplete="off"
                isInvalid={!!errors.visitFee}
                {...register("visitFee")}
              />
              <Form.Control.Feedback type="invalid">
                {errors.visitFee?.message}
              </Form.Control.Feedback>
            </FloatingLabel>
          </Form.Group>
        </Col>
      </Row>

      <Row className="gx-2 gy-3 my-1 ">
        <Col className="d-flex justify-content-end">
          <Button
            variant="link"
            className="text-secondary fw-medium p-0 me-3"
            type="button"
            onClick={() => navigate(-1)}
          >
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Ok
          </Button>
        </Col>
      </Row>
      {/* </Form>
      </FormProvider> */}
    </>
  );
};

export default VisitBasicInformation;
